import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, User, Settings2, Trash2, Camera } from 'lucide-react';
import { Resource, getResources } from '../lib/resources';
import { useBusiness } from '../contexts/BusinessContext';
import SelectResourceModal from './manage/SelectResourceModal';

interface DefaultResource {
  id: string;
  name: string;
}

interface AdvancedRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  stepTitle: string;
  defaultResources?: DefaultResource[];
  onAssignResources: (resources: DefaultResource[]) => void;
  photosEnabled?: boolean;
  onPhotosChange?: (enabled: boolean) => void;
}

export default function AdvancedRulesModal({
  isOpen,
  onClose,
  stepTitle,
  defaultResources,
  onAssignResources,
  photosEnabled = false,
  onPhotosChange
}: AdvancedRulesModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResources, setSelectedResources] = useState<DefaultResource[]>(defaultResources || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSelectResourceModal, setShowSelectResourceModal] = useState(false);
  const [photosEnabledState, setPhotosEnabledState] = useState(photosEnabled);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadResources();
    }
  }, [isOpen]);

  useEffect(() => {
    // Update selected resources when prop changes
    setSelectedResources(defaultResources || []);
  }, [defaultResources]);

  useEffect(() => {
    // Update photos enabled state when prop changes
    setPhotosEnabledState(photosEnabled);
  }, [photosEnabled]);

  const loadResources = async () => {
    if (!selectedBusiness?.id) return;

    setLoading(true);
    try {
      const data = await getResources(selectedBusiness.id);
      setResources(data);
    } catch (error) {
      console.error('Error loading resources:', error);
      setError('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 150);
  };

  const handleSave = () => {
    console.log('🔧 AdvancedRulesModal handleSave called');
    console.log('📸 Photos enabled state:', photosEnabledState);
    console.log('🔗 onPhotosChange callback exists:', !!onPhotosChange);
    
    onAssignResources(selectedResources);
    if (onPhotosChange) {
      console.log('📸 Calling onPhotosChange with:', photosEnabledState);
      onPhotosChange(photosEnabledState);
    } else {
      console.log('❌ onPhotosChange callback is missing!');
    }
    handleClose();
  };

  const handleRemoveResource = (resourceId: string) => {
    setSelectedResources(selectedResources.filter(r => r.id !== resourceId));
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[10000] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className={`fixed inset-0 bg-gray-500 transition-opacity duration-150 ${
            isAnimating ? 'bg-opacity-75' : 'bg-opacity-0'
          }`}
          onClick={handleClose}
        />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div
          className={`inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all duration-150 sm:my-8 sm:align-middle sm:max-w-lg sm:w-full ${
            isAnimating ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'
          }`}
        >
          {/* Header */}
          <div className="px-4 pt-5 pb-4 sm:p-6 bg-gradient-to-br from-blue-50 to-purple-50 border-b border-blue-200/30">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Advanced Rules
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Configure default settings for "{stepTitle}"
                </p>
              </div>
              <button
                onClick={handleClose}
                className="rounded-xl text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 p-2 hover:bg-gray-100/80 transition-all duration-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Default Resource Assignment */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Resource Assignment
              </label>
              <p className="text-xs text-gray-500 mb-4">
                These resources will be automatically assigned when creating bookings, but can be changed if needed.
              </p>
              
              {/* Selected Resources */}
              {selectedResources.length > 0 && (
                <div className="space-y-2 mb-4">
                  {selectedResources.map((resource) => (
                    <div key={resource.id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <User className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{resource.name}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveResource(resource.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Remove resource"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add Resource Button */}
              <button
                onClick={() => setShowSelectResourceModal(true)}
                className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-all duration-200 flex items-center justify-center"
              >
                <User className="h-5 w-5 mr-2" />
                Add Default Resource
              </button>
            </div>

            {/* Photos Rule */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Photos
              </label>
              <p className="text-xs text-gray-500 mb-4">
                Enable photo capture functionality for this workflow step.
              </p>
              
              <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200/30">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={photosEnabledState}
                    onChange={(e) => {
                      console.log('📸 Checkbox changed to:', e.target.checked);
                      setPhotosEnabledState(e.target.checked);
                    }}
                    className="w-4 h-4 text-blue-600 bg-white border-blue-300 rounded focus:ring-blue-500 focus:ring-2 mr-3"
                  />
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                      photosEnabledState 
                        ? 'bg-blue-100 text-blue-600' 
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      <Camera className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        Enable Photo Capture
                      </span>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {photosEnabledState 
                          ? 'Photos can be captured during this step' 
                          : 'Photo capture is disabled for this step'
                        }
                      </p>
                    </div>
                  </div>
                </label>
                
                {photosEnabledState && (
                  <div className="mt-3 p-3 bg-white/60 rounded-lg border border-blue-200/50">
                    <div className="flex items-start">
                      <Settings2 className="h-4 w-4 text-blue-500 mt-0.5 mr-2" />
                      <div>
                        <p className="text-xs font-medium text-blue-700">Photo Settings</p>
                        <p className="text-xs text-blue-600 mt-1">
                          Advanced photo capture settings will be available in future updates.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Future expansion area */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-start">
                <Settings2 className="h-5 w-5 text-gray-400 mt-0.5 mr-2" />
                <div>
                  <p className="text-sm font-medium text-gray-700">More settings coming soon</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Additional rules and automation options will be available in future updates.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
            <button
              type="button"
              onClick={handleSave}
              className="w-full inline-flex justify-center rounded-2xl border border-transparent shadow-sm px-6 py-3 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm transition-all duration-200"
            >
              Save Rules
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="mt-3 w-full inline-flex justify-center rounded-2xl border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      {showSelectResourceModal && createPortal(
        <SelectResourceModal
          isOpen={true}
          onClose={() => setShowSelectResourceModal(false)}
          onConfirm={({ resources }) => {
            setSelectedResources(
              resources.map(r => ({
                id: r.id,
                name: `${r.first_name} ${r.last_name}`,
              }))
            );
          }}
          selectedResources={selectedResources}
          confirmLabel="Confirm"
        />,
        document.body
      )}
    </>
  );
}